import type { JSX } from 'react';

import type { ToastOptions } from 'react-toastify';
import { toast } from 'react-toastify';

import type { Adaptive } from '@/components/types/Adaptive';
import type { Color } from '@/components/types/Color';
import type { FontWeight } from '@/components/types/FontWeight';
import type { Radius } from '@/components/types/Radius';
import type { TextSize } from '@/components/types/TextSize';
import { componentsConfig } from '@/config/componentsConfig';

import Text from '../Text';
import VStack from '../VStack';

type ToastContentProps = {
    message: string;
    title?: string;
    titleSize?: Adaptive<TextSize>;
    messageSize?: Adaptive<TextSize>;
    titleColor?: Color;
    messageColor?: Color;
    titleWeight?: Adaptive<FontWeight>;
    messageWeight?: Adaptive<FontWeight>;
    radius?: Adaptive<Radius>;
    options?: ToastOptions;
};

export function ToastContent({
    message,
    title,
    titleSize = componentsConfig.toast.titleSize,
    messageSize = componentsConfig.toast.messageSize,
    titleColor = componentsConfig.toast.titleColor,
    messageColor = componentsConfig.toast.messageColor,
    titleWeight = componentsConfig.toast.titleWeight,
    messageWeight = componentsConfig.toast.messageWeight,
}: ToastContentProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div className="flex items-start gap-3 w-full">
            <VStack vGap={1} className="w-full">
                {title && (
                    <Text weight={titleWeight} size={titleSize} color={titleColor}>
                        {title}
                    </Text>
                )}

                <Text weight={messageWeight} size={messageSize} color={messageColor} className="line-clamp-3 leading-4">
                    {message}
                </Text>
            </VStack>
        </div>
    );
}

export function showSuccessToast(toastContent: ToastContentProps): void {
    toast.success(ToastContent(toastContent), {
        ...toastContent.options,
    });
}

export function showErrorToast(toastContent: ToastContentProps): void {
    toast.error(ToastContent(toastContent), {
        ...toastContent.options,
    });
}

export function showInfoToast(toastContent: ToastContentProps): void {
    toast.info(ToastContent(toastContent), {
        ...toastContent.options,
    });
}

export function showWarningToast(toastContent: ToastContentProps): void {
    toast.warning(ToastContent(toastContent), {
        ...toastContent.options,
    });
}

export function showCustomToast({ children, options }: { children: JSX.Element; options?: ToastOptions }): void {
    toast(children, {
        ...options,
    });
}
